import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import s from './Feedback.module.css';

/**
 * Feedback em card — o unico canal de retorno visivel da aplicacao.
 *
 * Nunca `alert()`, `confirm()`, `console.log` como se fosse aviso ao usuario,
 * nem texto de status solto num `<span>` que ninguem ve. Toda chamada de rede
 * termina num card, inclusive quando falha.
 */

export type Variant = 'success' | 'error' | 'warning' | 'info' | 'loading';

export interface CardAction {
  label: string;
  onClick: () => void;
  tone?: 'primary' | 'ghost' | 'danger';
}

interface CardInput {
  variant: Variant;
  title: string;
  detail?: string;
  actions?: CardAction[];
  /** `null` desliga o auto-dismiss. Erro e loading ja nascem sem ele. */
  durationMs?: number | null;
}

interface Card extends CardInput {
  id: number;
}

interface ConfirmOptions {
  title: string;
  detail?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Deixa o botao de confirmacao vermelho. Use quando algo sera perdido. */
  destructive?: boolean;
}

/** Handle de um card de carregamento, para virar resultado no mesmo lugar. */
export interface LoadingCard {
  resolve: (variant: Exclude<Variant, 'loading'>, title: string, detail?: string) => void;
  dismiss: () => void;
}

interface FeedbackApi {
  success: (title: string, detail?: string) => number;
  error: (title: string, detail?: string) => number;
  warning: (title: string, detail?: string) => number;
  info: (title: string, detail?: string) => number;
  loading: (title: string, detail?: string) => LoadingCard;
  show: (card: CardInput) => number;
  dismiss: (id: number) => void;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const FeedbackContext = createContext<FeedbackApi | null>(null);

/**
 * Erro fica quase o dobro do tempo na tela: costuma trazer detalhe que a pessoa
 * precisa ler com calma, e as vezes anotar.
 */
const DEFAULT_DURATION_MS = 5000;
const ERROR_DURATION_MS = 9000;

const ICONS: Record<Variant, ReactNode> = {
  success: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12.2 2.4 2.4 4.6-4.9" />
    </svg>
  ),
  error: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.8v4.8M12 16.2h.01" />
    </svg>
  ),
  warning: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.3 3.9 2.6 17.1A2 2 0 0 0 4.3 20h15.4a2 2 0 0 0 1.7-2.9L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  ),
  info: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16.2v-4.8M12 7.8h.01" />
    </svg>
  ),
  loading: <span className={s.spinner} aria-hidden="true" />,
};

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [cards, setCards] = useState<Card[]>([]);
  const [confirmState, setConfirmState] = useState<
    (ConfirmOptions & { resolve: (value: boolean) => void }) | null
  >(null);

  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setCards((current) => current.filter((card) => card.id !== id));
  }, []);

  const schedule = useCallback(
    (id: number, durationMs: number | null) => {
      if (durationMs === null) return;
      timers.current.set(
        id,
        setTimeout(() => {
          dismiss(id);
        }, durationMs),
      );
    },
    [dismiss],
  );

  const show = useCallback(
    (input: CardInput): number => {
      const id = nextId.current++;
      const durationMs =
        input.durationMs !== undefined
          ? input.durationMs
          : input.variant === 'loading'
            ? null
            : // Card com acao nao some sozinho: a acao ficaria inalcancavel.
              input.actions?.length
              ? null
              : input.variant === 'error'
                ? ERROR_DURATION_MS
                : DEFAULT_DURATION_MS;

      setCards((current) => [...current, { ...input, id, durationMs }]);
      schedule(id, durationMs);
      return id;
    },
    [schedule],
  );

  const api = useMemo<FeedbackApi>(
    () => ({
      show,
      dismiss,
      success: (title, detail) => show({ variant: 'success', title, detail }),
      error: (title, detail) => show({ variant: 'error', title, detail }),
      warning: (title, detail) => show({ variant: 'warning', title, detail }),
      info: (title, detail) => show({ variant: 'info', title, detail }),

      loading: (title, detail) => {
        const id = show({ variant: 'loading', title, detail, durationMs: null });
        return {
          // Vira resultado no mesmo card, sem piscar a tela nem empilhar
          // uma segunda mensagem sobre a primeira.
          resolve: (variant, nextTitle, nextDetail) => {
            const durationMs = variant === 'error' ? ERROR_DURATION_MS : DEFAULT_DURATION_MS;
            setCards((current) =>
              current.map((card) =>
                card.id === id
                  ? { ...card, variant, title: nextTitle, detail: nextDetail, durationMs }
                  : card,
              ),
            );
            schedule(id, durationMs);
          },
          dismiss: () => {
            dismiss(id);
          },
        };
      },

      confirm: (options) =>
        new Promise<boolean>((resolve) => {
          setConfirmState({ ...options, resolve });
        }),
    }),
    [show, dismiss, schedule],
  );

  const closeConfirm = (value: boolean) => {
    confirmState?.resolve(value);
    setConfirmState(null);
  };

  return (
    <FeedbackContext.Provider value={api}>
      {children}

      <div className={s.region}>
        {cards.map((card) => (
          <div
            key={card.id}
            className={s.card}
            data-variant={card.variant}
            // Erro interrompe o leitor de tela; o resto so anuncia na proxima
            // pausa, para nao atropelar o que a pessoa esta fazendo.
            role={card.variant === 'error' ? 'alert' : 'status'}
            aria-live={card.variant === 'error' ? 'assertive' : 'polite'}
          >
            <span className={s.icon}>{ICONS[card.variant]}</span>

            <div className={s.body}>
              <p className={s.title}>{card.title}</p>
              {card.detail ? <p className={s.detail}>{card.detail}</p> : null}

              {card.actions?.length ? (
                <div className={s.actions}>
                  {card.actions.map((action) => (
                    <button
                      key={action.label}
                      type="button"
                      className={[
                        s.action,
                        action.tone === 'ghost' ? s.actionGhost : '',
                        action.tone === 'danger' ? s.actionDanger : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => {
                        action.onClick();
                        dismiss(card.id);
                      }}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <button
              type="button"
              className={s.close}
              aria-label="Fechar aviso"
              onClick={() => {
                dismiss(card.id);
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>

            {card.durationMs ? (
              <span className={s.timer} style={{ animationDuration: `${String(card.durationMs)}ms` }} />
            ) : null}
          </div>
        ))}
      </div>

      {confirmState ? (
        <div
          className={s.backdrop}
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) closeConfirm(false);
          }}
        >
          <div className={s.dialog} role="alertdialog" aria-modal="true" aria-label={confirmState.title}>
            <h2 className={s.dialogTitle}>{confirmState.title}</h2>
            {confirmState.detail ? <p className={s.dialogDetail}>{confirmState.detail}</p> : null}

            <div className={s.dialogActions}>
              <button
                type="button"
                className={`${s.action} ${s.actionGhost}`}
                onClick={() => {
                  closeConfirm(false);
                }}
              >
                {confirmState.cancelLabel ?? 'Cancelar'}
              </button>
              <button
                type="button"
                className={`${s.action} ${confirmState.destructive ? s.actionDanger : ''}`}
                autoFocus
                onClick={() => {
                  closeConfirm(true);
                }}
              >
                {confirmState.confirmLabel ?? 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </FeedbackContext.Provider>
  );
}

export function useFeedback(): FeedbackApi {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback precisa estar dentro de <FeedbackProvider>.');
  }
  return context;
}
